import { toCSV, toMapCSV, uuid } from 'editor/core/highcharts-editor';
import { cloneDeep, isArray, isFunction } from 'lodash';

export const stringifyFn = (obj: any, tabs: any) => {
  return JSON.stringify(
    obj,
    function (key, value) {
      if (isFunction(value)) {
        return value.toString();
      }

      return value;
    },
    tabs
  );
};

/** Get embeddable SVG
 *  @returns {string} - the result from `Highcharts.Chart.getSVG()`
 */
export const getEmbeddableSVG = (chartConfig: any) => {
  const { chart } = chartConfig;
  return isFunction(chart.getSVG) ? chart.getSVG() : '';
};

/** Get embeddable JSON
 *  This returns the merged chart, with both customized options
 *  and options set indirectly through templates.
 *  @returns {object} - the chart object
 */
export const getEmbeddableJSON = (projectConfig: any) => {
  const { aggregatedOptions } = projectConfig;

  const r: any = cloneDeep(aggregatedOptions);

  // This should be part of the series
  if (r && isArray(r.series)) {
    r.series = r.series.map(function (s: any) {
      delete s.data;
      return s;
    });
  }

  return r;
};

/** Get embeddable JavaScript
 *  @param id {string} - the ID of the node to attach the chart to
 *  @returns {string} - a string containing JavaScript to reproduce the chart
 */
export const getEmbeddableJavaScript = (projectConfig: any, chartConfig: any, id: string) => {
  const { constr, customCode, customCodeStr } = chartConfig;
  const { customizedOptions } = projectConfig;

  const cdnIncludes = [
    'https://code.highcharts.com/stock/highstock.js',
    'https://code.highcharts.com/highcharts-more.js',
    'https://code.highcharts.com/highcharts-3d.js',
    'https://code.highcharts.com/modules/data.js',
    'https://code.highcharts.com/modules/exporting.js',
    'https://code.highcharts.com/modules/funnel.js',
    'https://code.highcharts.com/modules/annotations.js',
    'https://code.highcharts.com/stock/modules/annotations-advanced.js',
    'https://code.highcharts.com/stock/modules/stock-tools.js',
    'https://code.highcharts.com/modules/accessibility.js',
    'https://code.highcharts.com/modules/solid-gauge.js'
  ];

  const cdnIncludesArr = [
    'var files = ',
    JSON.stringify(cdnIncludes),
    ',',
    'loaded = 0; ',
    'if (typeof window["HighchartsEditor"] === "undefined") {',
    'window.HighchartsEditor = {',
    'ondone: [cl],',
    'hasWrapped: false,',
    'hasLoaded: false',
    '};',
    'include(files[0]);',
    '} else {',
    'if (window.HighchartsEditor.hasLoaded) {',
    'cl();',
    '} else {',
    'window.HighchartsEditor.ondone.push(cl);',
    '}',
    '}',
    'function isScriptAlreadyIncluded(src){',
    'var scripts = document.getElementsByTagName("script");',
    'for (var i = 0; i < scripts.length; i++) {',
    'if (scripts[i].hasAttribute("src")) {',
    'if ((scripts[i].getAttribute("src") || "").indexOf(src) >= 0 || (scripts[i].getAttribute("src") === "http://code.highcharts.com/highcharts.js" && src === "https://code.highcharts.com/stock/highstock.js")) {',
    'return true;',
    '}',
    '}',
    '}',
    'return false;',
    '}',
    'function check() {',
    'if (loaded === files.length) {',
    'for (var i = 0; i < window.HighchartsEditor.ondone.length; i++) {',
    'try {',
    'window.HighchartsEditor.ondone[i]();',
    '} catch(e) {',
    'console.error(e);',
    '}',
    '}',
    'window.HighchartsEditor.hasLoaded = true;',
    '}',
    '}',

    'function include(script) {',
    'function next() {',
    '++loaded;',
    'if (loaded < files.length) {',
    'include(files[loaded]);',
    '}',
    'check();',
    '}',
    'if (isScriptAlreadyIncluded(script)) {',
    'return next();',
    '}',
    'var sc=document.createElement("script");',
    'sc.src = script;',
    'sc.type="text/javascript";',
    'sc.onload=function() { next(); };',
    'document.head.appendChild(sc);',
    '}',

    'function each(a, fn){',
    'if (typeof a.forEach !== "undefined"){a.forEach(fn);}',
    'else{',
    'for (var i = 0; i < a.length; i++){',
    'if (fn) {fn(a[i]);}',
    '}',
    '}',
    '}',

    'var inc = {},incl=[]; each(document.querySelectorAll("script"), function(t) {inc[t.src.substr(0, t.src.indexOf("?"))] = 1; ',
    '});'
  ];

  id = id || '';

  const chartConstr = constr ?? 'Chart';

  return (
    '\n' +
    [
      '(function(){ ',

      cdnIncludesArr.join(''),

      ' function cl() {',
      'if(typeof window["Highcharts"] !== "undefined"){',

      !customizedOptions.lang ? '' : 'Highcharts.setOptions({lang:' + JSON.stringify(customizedOptions.lang) + '});',
      'var options=',
      stringifyFn(getEmbeddableJSON(projectConfig), null),
      ';',
      isFunction(customCode) ? customCodeStr : '',
      'new Highcharts.' + chartConstr + '("',
      id,
      '", options);',
      '}',
      '}',
      '})();'
    ].join('') +
    '\n'
  );
};

/** Get embeddable HTML
 *  @returns {string} - a string of embeddable HTML
 */
export const getEmbeddableHTML = (projectConfig: any, chartConfig: any) => {
  const id = 'highcharts-' + uuid();
  return (
    ['<div id="', id, '">', '</div>'].join('') +
    '<script>' +
    getEmbeddableJavaScript(projectConfig, chartConfig, id) +
    '</script>'
  );
};

/** Download CSV
 *  @param data {array} - the chart data
 *  @param data {string} - optional delimiter (default: ',')
 *  @param data {boolean} - If chart is map, need to do different csv parsing.
 *  @returns {null}
 */
export const getCSV = (dataOptions: any, delimiter?: string, isMap?: boolean) => {
  const csv = isMap ? toMapCSV(dataOptions, delimiter ?? ',') : toCSV(dataOptions, delimiter ?? ',');
  return csv;
};
